import React, { memo, useCallback, useMemo, useState } from 'react';
import { AudioElement as AudioElementModel } from '@bynder-studio/render-core';
import { Button, Dropdown, Form, List } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { IconCut, IconMore, IconReplace } from '@bynder/icons';
import useVariations from 'packages/pages/design/hooks/useVariations';
import useElement from 'packages/pages/design/hooks/useElement';
import useDesign from 'packages/pages/design/hooks/useDesign';
import useAudioHook from 'packages/hooks/useElementAudio';
import { getElementPropertiesFromSource } from '~/common/editor/helpers';
import { ContentType } from 'packages/pages/editor/RightSideMenu/FormComponents/BrowseButton/types';
import { CONTENT_MODAL_STEPS } from 'packages/pages/editor/ContentPickerModal/types';
import ContentPickerModal from 'packages/pages/editor/ContentPickerModal';
import { AudioFeature } from 'packages/pages/editor/RightSideMenu/FormComponents/AudioFeature';

const formatTime = (time: number) => {
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

type Props = {
    id: number;
    disabled: boolean;
    isLoading: boolean;
};

const AudioElement = ({ id, disabled, isLoading }: Props) => {
    const { translate } = useTranslate();
    const { frameRate, creativeType } = useDesign();
    const { element, updateElement, syncContentProperty } = useElement<AudioElementModel>(id);
    const { removeDeletedAsset } = useVariations();

    const [showModal, setShowModal] = useState(false);
    const [modalStep, setModalStep] = useState<CONTENT_MODAL_STEPS>(CONTENT_MODAL_STEPS.CHOICE);

    const updateFn = useCallback(
        (params) => {
            if (element?.contentPropertyId) {
                syncContentProperty(params);
            } else {
                updateElement(params);
            }
        },
        [element?.contentPropertyId, syncContentProperty, updateElement],
    );

    const { gain, fadeIn, fadeOut, setGain, setFadeInOut } = useAudioHook({
        audio: element as AudioElementModel,
        onAudioChange: updateFn,
    });

    const handleSelectSource = useCallback(
        (source) => {
            const fieldsToChange = getElementPropertiesFromSource(element, creativeType, source);
            delete fieldsToChange.duration;

            updateFn(fieldsToChange);
            removeDeletedAsset(id);
        },
        [updateFn, element, creativeType, removeDeletedAsset, id],
    );

    const fileName = element?.fileName || '';
    const offsetTime = element?.offsetTime ?? 0;
    const duration = element?.duration ?? 0;

    const startAt = useMemo(() => formatTime(offsetTime / 1000), [offsetTime]);
    const endAt = useMemo(
        () => formatTime(offsetTime / 1000 + duration / frameRate),
        [offsetTime, duration, frameRate],
    );

    const openModal = useCallback((step: CONTENT_MODAL_STEPS) => {
        setModalStep(step);
        setShowModal(true);
    }, []);

    return (
        <>
            {showModal && (
                <ContentPickerModal
                    type={ContentType.AUDIO}
                    show={showModal}
                    modalStep={modalStep}
                    frameRate={frameRate}
                    selectedObject={{ element, duration }}
                    collectionId={element?.virtualData?.bynderCollectionId}
                    toggleModal={setShowModal}
                    confirmSelection={(item) => {
                        setShowModal(false);
                        handleSelectSource(item);
                    }}
                    resizableRange={false}
                    isEditorPage={false}
                />
            )}
            <List.Item
                subtext={fileName ? `${startAt} - ${endAt}` : undefined}
                rightElements={
                    <Dropdown
                        trigger={({ isOpen: isDropdownOpen, ...triggerProps }) => (
                            <Button
                                isPressed={isDropdownOpen}
                                isDisabled={disabled || isLoading}
                                variant="clean"
                                icon={<IconMore />}
                                aria-label="Actions"
                                {...triggerProps}
                            />
                        )}
                        position="bottom-right"
                    >
                        <Dropdown.Item onClick={() => openModal(CONTENT_MODAL_STEPS.CHOICE)} icon={<IconReplace />}>
                            {translate('editor.sidebar.shots.asset.card.label')}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => openModal(CONTENT_MODAL_STEPS.TRIM)} icon={<IconCut />}>
                            {translate('editor.sidebar.shots.asset.card.audio.action.label')}
                        </Dropdown.Item>
                    </Dropdown>
                }
            >
                {fileName || translate('editor.element.content.placeholder.audio')}
            </List.Item>
            <Form.Group>
                <AudioFeature
                    gain={gain}
                    fadeIn={fadeIn}
                    fadeOut={fadeOut}
                    onFadeInChange={setFadeInOut('fadeIn')}
                    onFadeOutChange={setFadeInOut('fadeOut')}
                    onGainChange={setGain}
                    isDisabled={disabled}
                />
            </Form.Group>
        </>
    );
};

export default memo(AudioElement);
